/**
 * Booking logic: the free slots a meeting type offers, and what a booking writes.
 * - Slots come from each host's booking hours (in the vendor's time zone), minus the meetings
 *   the host already has in zInspector (Follow-up tasks, kept apart by the buffer), minus
 *   anything sooner than the minimum notice.
 * - A booking is a lead (Prospect task, saved at step 1 so an abandoned booking isn't lost) and
 *   a meeting: a Follow-up task on the host's calendar, which zInspector syncs to Google Calendar.
 */
import type { Identifier } from "ra-core";

import type { BookingSettings, MeetingType } from "./bookingConfig";
import { TASK_TYPE_PK } from "./crmConfig";

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DEFAULT_PRIORITY = 3;

/** Offset of a time zone from UTC at an instant, in ms (positive east of UTC). */
const zoneOffset = (utcMs: number, timeZone: string): number => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
      .formatToParts(new Date(utcMs))
      .map((p) => [p.type, p.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - Math.floor(utcMs / 1000) * 1000;
};

/** "2026-10-13" + "10:00" in a time zone → the UTC instant (ms). */
export const zonedTime = (
  date: string,
  hhmm: string,
  timeZone: string,
): number => {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = hhmm.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const first = guess - zoneOffset(guess, timeZone);
  // Again at the result, in case a daylight-saving change sits between the two.
  return guess - zoneOffset(first, timeZone);
};

/** The calendar date ("YYYY-MM-DD") and weekday (0 = Sunday) of an instant in a time zone. */
export const zonedDate = (
  utcMs: number,
  timeZone: string,
): { date: string; weekday: number } => {
  const local = new Date(utcMs + zoneOffset(utcMs, timeZone));
  return {
    date: local.toISOString().slice(0, 10),
    weekday: local.getUTCDay(),
  };
};

export interface BusyTime {
  hostId: Identifier;
  start: string;
  end: string;
}

export interface Slot {
  start: string;
  end: string;
  hostId: number;
}

/**
 * Every bookable start time for a meeting type, earliest first. When several hosts are free at
 * the same time, the one with the fewest meetings that day takes it.
 */
export const freeSlots = (
  settings: BookingSettings,
  type: MeetingType,
  busy: BusyTime[],
  now = new Date(),
): Slot[] => {
  const length = type.minutes * MINUTE_MS;
  const buffer = settings.bufferMinutes * MINUTE_MS;
  const earliest = now.getTime() + settings.minNoticeHours * HOUR_MS;
  const busyOf = (hostId: number) =>
    busy
      .filter((b) => String(b.hostId) === String(hostId))
      .map((b) => [Date.parse(b.start), Date.parse(b.end)] as const);
  // Start time → the slot and how busy its host already is that day.
  const slots = new Map<number, { slot: Slot; load: number }>();

  for (let day = 0; day <= settings.daysAhead; day++) {
    const { date, weekday } = zonedDate(
      now.getTime() + day * DAY_MS,
      settings.timeZone,
    );
    for (const hostId of type.hostIds) {
      const window = settings.hours.find((h) => h.hostId === hostId)?.days[
        String(weekday)
      ];
      if (!window) continue;
      const open = zonedTime(date, window[0], settings.timeZone);
      const close = zonedTime(date, window[1], settings.timeZone);
      const taken = busyOf(hostId);
      const load = taken.filter(
        ([start]) => start >= open && start < close,
      ).length;
      const step = settings.slotStepMinutes * MINUTE_MS;
      for (let start = open; start + length <= close; start += step) {
        const end = start + length;
        if (start < earliest) continue;
        if (taken.some(([b0, b1]) => start < b1 + buffer && end + buffer > b0))
          continue;
        const current = slots.get(start);
        if (current && current.load <= load) continue;
        slots.set(start, {
          slot: {
            start: new Date(start).toISOString(),
            end: new Date(end).toISOString(),
            hostId,
          },
          load,
        });
      }
    }
  }
  return [...slots.values()]
    .map((s) => s.slot)
    .sort((a, b) => a.start.localeCompare(b.start));
};

export interface BookingAnswers {
  person: string;
  email: string;
  company?: string;
  /**
   * The client's zInspector contact, when the duplicate checks found one: assigned to the task,
   * zInspector's calendar sync sends them the invite too.
   */
  contactId?: Identifier | null;
  /** Custom questions, by their text. */
  custom: { question: string; answer: string }[];
}

/** The two writes that put a booked meeting on the host's calendar (create, then edit). */
export const buildMeetingWrites = (
  type: MeetingType,
  slot: Slot,
  lead: { id: Identifier; company_id?: Identifier | null },
  answers: BookingAnswers,
): { create: Record<string, unknown>; edit: Record<string, unknown> } => ({
  create: {
    Action: TASK_TYPE_PK["Follow-up"],
    Priority: DEFAULT_PRIORITY,
    Property: lead.company_id ? Number(lead.company_id) : undefined,
    Description: `${type.name} with ${answers.person}${
      answers.company ? ` (${answers.company})` : ""
    }`,
    StartDate: slot.start,
    EndDate: slot.end,
    // zInspector's own Google Calendar sync puts it on the host's calendar.
    IncludeCalendar: true,
    ManagedBy: slot.hostId,
    AssignedToUser: slot.hostId,
    AssignedToContact: answers.contactId
      ? Number(answers.contactId)
      : undefined,
  },
  edit: {
    EntranceInstruction: "Meeting",
    Notes: [
      `Booked in zCRM by ${answers.person} <${answers.email}> (lead ${lead.id}).`,
      `${type.location}: ${type.joinInfo}`,
      ...answers.custom
        .filter((c) => c.answer.trim())
        .map((c) => `${c.question} ${c.answer.trim()}`),
    ].join("\n"),
  },
});

const calendarStamp = (iso: string) =>
  iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");

/** "Add to Google Calendar" and "Add to Outlook" links for the confirmation. */
export const calendarLinks = (
  title: string,
  slot: Pick<Slot, "start" | "end">,
  details: string,
  location: string,
) => ({
  google: `https://calendar.google.com/calendar/render?${new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${calendarStamp(slot.start)}/${calendarStamp(slot.end)}`,
    details,
    location,
  })}`,
  outlook: `https://outlook.live.com/calendar/0/deeplink/compose?${new URLSearchParams(
    {
      subject: title,
      startdt: slot.start,
      enddt: slot.end,
      body: details,
      location,
    },
  )}`,
});
